import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import apiClient from '../../Services/ApiClient';
import { COLORS } from '../../Styles/Colors';

/** A stocking threshold: a whole number, or null for "not set". */
export type Threshold = number | null;

export type InventoryBin = {
  binLocationId: number;
  binLocationName: string;
  quantity: number | string;
  quantityMinimum: Threshold;
  quantityMaximum: Threshold;
};

/** The Location-level row the bins roll up into, as returned alongside them. */
export type BinLocationSummary = {
  quantityOnHand: number;
  quantityMinimum: Threshold;
  quantityMaximum: Threshold;
  isConsignment: boolean;
  adjusted?: { minimum: boolean; maximum: boolean };
};

const PRIMARY_BIN_ID = 0;

// Same palette the grid's "% Full" column uses, so a bar means the same thing
// wherever it appears.
const BAR_BELOW_MIN = '#E87722'; // Abatix Orange
const BAR_LOW = '#9E9E9E';       // Abatix Gray
const BAR_OK = '#008490';        // Abatix Teal

const formatQty = (value: number | string) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed.toLocaleString() : '0';
};

const formatThreshold = (value: Threshold) =>
  value === null || value === undefined ? '—' : value.toLocaleString();

/**
 * Expand/collapse arrow on inventory rows.
 *
 * Shown on every row, but only clickable where the location actually has bins
 * turned on. A greyed-out arrow with a tooltip says "nothing to see here" more
 * clearly than an arrow that vanishes on some rows and not others — while still
 * refusing to open a panel that could only ever be empty.
 */
export const BinToggleCell: React.FC<{
  enabled: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** When true, expanding this row opens the quantity / min / max editor. */
  canEdit?: boolean;
}> = ({ enabled, expanded, onToggle, canEdit = false }) => {
  if (!enabled) {
    return (
      // MUI Tooltip needs a non-disabled child to receive hover events, so the
      // button is visually and functionally inert rather than `disabled`.
      <Tooltip title="No bins at this location">
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            disableRipple
            sx={{
              color: '#e2e8f0',
              cursor: 'default',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={expanded ? 'Hide bins' : canEdit ? 'Adjust bin quantity, min, and max' : 'Show bins'}>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        sx={{
          color: expanded ? COLORS.teal : '#94a3b8',
          transform: expanded ? 'rotate(90deg)' : 'none',
          transition: 'transform 0.18s ease, color 0.18s ease',
          '&:hover': { color: COLORS.teal, backgroundColor: `${COLORS.teal}14` },
        }}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// The header bar spans the whole row like the legacy nested jsGrid, but the new
// grid is far wider than the old site — fixed widths keep the columns on screen
// instead of pushing them past the horizontal scroll.
const COL_WIDTHS = { name: 360, quantity: 140, min: 120, max: 120, pctFull: 160, spacer: 'auto' };

const headerCellSx = {
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#ffffff',
  borderBottom: 'none',
  py: 1,
};

const bodyCellSx = {
  fontSize: '0.82rem',
  borderBottom: '1px solid #e2e8f0',
  py: 0.9,
};

export type BinPanelMode = 'view' | 'manage';

/** One bin's pending edits. A field is absent until touched; '' means cleared. */
type BinDraft = {
  quantity?: number | '';
  quantityMinimum?: number | '';
  quantityMaximum?: number | '';
};

type DraftField = keyof BinDraft;

type Props = {
  /** Base bins endpoint, e.g. `/api/inventory/123/bins`. */
  endpoint: string;
  /** Qty on hand for the item — the ceiling all bins must stay under. */
  quantityOnHand: number;
  mode?: BinPanelMode;
  /** Defaults on so expanding a row is enough to type quantity / min / max. */
  canEdit?: boolean;
  /**
   * Abatix consignment inventory: its Location Min/Max is read-only everywhere
   * else, so the roll-up is not allowed to raise it from in here.
   */
  isConsignment?: boolean;
  onClose?: () => void;
  /** Called after a successful save so the parent grid can refresh. */
  onSaved?: () => void;
};

/**
 * Bin breakdown for one inventory row.
 *
 * Expanding a row on View Inventory opens this as an editor: quantity, min, and
 * max are inputs, every active bin at the location is listed (so stock can be
 * moved into an empty one), and the Primary Bin row is a live unallocated
 * remainder. Staff without edit permission still see the numbers, but Save is
 * disabled.
 *
 * Bin min/max roll up to the Location-level min/max: if the bin totals exceed
 * it, saving RAISES the Location value rather than rejecting the edit. That
 * changes Reorder Qty and % Full on the parent grid, so the footer warns before
 * the save and the banner reports it after — the one thing this panel must
 * never do is change the grid silently.
 */
export const BinDetailsPanel: React.FC<Props> = ({
  endpoint,
  quantityOnHand,
  mode = 'manage',
  canEdit = true,
  isConsignment = false,
  onClose,
  onSaved,
}) => {
  // Expanding the row is the editor. `mode` is kept for callers that still pass
  // it; view-only is only for users who cannot save.
  const isManaging = true;

  const [bins, setBins] = useState<InventoryBin[]>([]);
  const [location, setLocation] = useState<BinLocationSummary | null>(null);
  const [drafts, setDrafts] = useState<Record<number, BinDraft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchUrl = `${endpoint}${isManaging ? '?includeUnassigned=1' : ''}`;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient(fetchUrl);
        if (!response.ok) throw new Error('Failed to load bin locations');
        const payload = await response.json();
        if (!cancelled) {
          setBins(Array.isArray(payload?.data) ? payload.data : []);
          setLocation(payload?.location ?? null);
          setDrafts({});
          setSaveError(null);
          setSavedNotice(null);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load bin locations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchUrl]);

  const editableBins = useMemo(
    () => bins.filter((bin) => bin.binLocationId !== PRIMARY_BIN_ID),
    [bins],
  );

  // ---- effective values: the draft if the field was touched, else what is stored

  const qtyFor = (bin: InventoryBin): number => {
    const draft = drafts[bin.binLocationId]?.quantity;
    if (draft === '') return 0;
    return draft !== undefined ? draft : Number(bin.quantity ?? 0);
  };

  const thresholdFor = (bin: InventoryBin, field: 'quantityMinimum' | 'quantityMaximum'): Threshold => {
    const draft = drafts[bin.binLocationId]?.[field];
    if (draft === '') return null;
    return draft !== undefined ? draft : bin[field] ?? null;
  };

  const minFor = (bin: InventoryBin) => thresholdFor(bin, 'quantityMinimum');
  const maxFor = (bin: InventoryBin) => thresholdFor(bin, 'quantityMaximum');

  const isFieldEdited = (bin: InventoryBin, field: DraftField): boolean => {
    const draft = drafts[bin.binLocationId]?.[field];
    if (draft === undefined) return false;
    if (field === 'quantity') return qtyFor(bin) !== Number(bin.quantity ?? 0);
    return thresholdFor(bin, field as 'quantityMinimum' | 'quantityMaximum') !== (bin[field as 'quantityMinimum' | 'quantityMaximum'] ?? null);
  };

  const isBinEdited = (bin: InventoryBin) =>
    isFieldEdited(bin, 'quantity') || isFieldEdited(bin, 'quantityMinimum') || isFieldEdited(bin, 'quantityMaximum');

  // ---- derived totals, all tracking the drafts so they move as the user types

  const allocated = editableBins.reduce((sum, bin) => sum + qtyFor(bin), 0);
  const unallocated = Number(quantityOnHand ?? 0) - allocated;
  const overAllocated = unallocated < 0;

  const binMinTotal = editableBins.reduce((sum, bin) => sum + (minFor(bin) ?? 0), 0);
  const binMaxTotal = editableBins.reduce((sum, bin) => sum + (maxFor(bin) ?? 0), 0);

  const locationMin = location?.quantityMinimum ?? null;
  const locationMax = location?.quantityMaximum ?? null;

  // Raise-only: the bin totals can push the Location value up, never down.
  const willRaiseMin = binMinTotal > (locationMin ?? 0);
  const willRaiseMax = binMaxTotal > (locationMax ?? 0);
  const willRaise = willRaiseMin || willRaiseMax;

  // Setting a Location Min where there was none is what switches on low-stock
  // alerting for the item, so it gets its own wording.
  const willCreateLocationMin = willRaiseMin && locationMin === null;

  const invalidBin = editableBins.find((bin) => {
    const min = minFor(bin);
    const max = maxFor(bin);
    return min !== null && max !== null && min > max;
  });

  const consignmentBlocked = isConsignment && willRaise;
  const hasEdits = editableBins.some(isBinEdited);

  const handleChange = (binLocationId: number, field: DraftField, raw: string) => {
    setSaveError(null);
    setSavedNotice(null);
    setDrafts((prev) => ({
      ...prev,
      [binLocationId]: {
        ...(prev[binLocationId] || {}),
        [field]: raw === '' ? '' : Math.max(0, Math.trunc(Number(raw) || 0)),
      },
    }));
  };

  const handleCancel = () => {
    setDrafts({});
    setSaveError(null);
    setSavedNotice(null);
    onClose?.();
  };

  const handleSave = async () => {
    if (overAllocated || invalidBin || consignmentBlocked || saving) return;

    setSaving(true);
    setSaveError(null);
    setSavedNotice(null);

    try {
      // Send only the bins, and only the fields, that actually changed. The API
      // fills the rest in from what is stored, so a min/max edit cannot clobber
      // a quantity.
      const payload = editableBins
        .filter(isBinEdited)
        .map((bin) => {
          const entry: Record<string, unknown> = { binLocationId: bin.binLocationId };
          if (isFieldEdited(bin, 'quantity')) entry.quantity = qtyFor(bin);
          if (isFieldEdited(bin, 'quantityMinimum')) entry.quantityMinimum = minFor(bin);
          if (isFieldEdited(bin, 'quantityMaximum')) entry.quantityMaximum = maxFor(bin);
          return entry;
        });

      const response = await apiClient(endpoint, {
        method: 'PUT',
        body: JSON.stringify({ bins: payload }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        setSaveError(body?.error || 'Failed to save bin quantities');
        return;
      }

      setBins(Array.isArray(body?.data) ? body.data : bins);
      setLocation(body?.location ?? location);
      setDrafts({});

      // The roll-up may have moved a value the user cannot see from in here —
      // say so rather than letting the grid change on its own.
      const adjusted = body?.location?.adjusted;
      if (adjusted?.minimum || adjusted?.maximum) {
        const parts = [
          adjusted.minimum ? `Location Min raised to ${formatQty(body.location.quantityMinimum)}` : null,
          adjusted.maximum ? `Location Max raised to ${formatQty(body.location.quantityMaximum)}` : null,
        ].filter(Boolean);
        setSavedNotice(`Saved. ${parts.join(' · ')}.`);
      }

      onSaved?.();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save bin quantities');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: '#f8fafc',
          borderLeft: `3px solid ${COLORS.teal}`,
        }}
      >
        <CircularProgress size={16} sx={{ color: COLORS.teal }} />
        <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>Loading bins…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8fafc', borderLeft: `3px solid ${COLORS.teal}` }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#c62828' }}>{error}</Typography>
      </Box>
    );
  }

  // The API always returns the synthetic Primary Bin, so `bins.length` is never
  // 0 — "has no bins" means "has no bins other than the derived one". Rendering
  // a lone Primary Bin row explains nothing, so say what is actually going on.
  // (Locations with bins turned off never reach here — BinToggleCell refuses to
  // open the panel for them.)
  if (editableBins.length === 0) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.75,
          backgroundColor: '#f8fafc',
          borderLeft: `3px solid ${COLORS.teal}`,
        }}
      >
        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600 }}>
          No bins set up for this location
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', mt: 0.25 }}>
          {`All ${formatQty(quantityOnHand)} on hand sits unallocated in the Primary Bin. `}
          Add bins on the Bin Locations page to track quantities and Min/Max per bin.
        </Typography>
      </Box>
    );
  }

  const numberInputSx = (edited: boolean, invalid: boolean) => ({
    width: 90,
    textAlign: 'center' as const,
    borderRadius: '4px',
    padding: '4px',
    outline: 'none',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#1e293b',
    border: invalid ? '1px solid #c62828' : edited ? `1px solid ${COLORS.teal}` : '1px solid #cbd5e1',
    backgroundColor: invalid ? '#fdecea' : edited ? `${COLORS.teal}10` : 'white',
  });

  const renderQuantity = (bin: InventoryBin) => {
    const isPrimary = bin.binLocationId === PRIMARY_BIN_ID;

    if (isPrimary) {
      const value = isManaging ? unallocated : Number(bin.quantity ?? 0);
      return (
        <Typography
          component="span"
          sx={{
            fontSize: '0.82rem',
            fontWeight: 600,
            color: value < 0 ? '#c62828' : value === 0 ? '#cbd5e1' : COLORS.teal,
          }}
        >
          {formatQty(value)}
        </Typography>
      );
    }

    const edited = isFieldEdited(bin, 'quantity');

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {edited && (
          <Box component="span" sx={{ fontSize: '8px', color: COLORS.teal, fontWeight: 700 }}>
            ●
          </Box>
        )}
        <Box
          component="input"
          type="number"
          min={0}
          step={1}
          value={String(qtyFor(bin))}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(bin.binLocationId, 'quantity', e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
          }}
          sx={numberInputSx(edited, false)}
        />
      </Box>
    );
  };

  const renderThreshold = (bin: InventoryBin, field: 'quantityMinimum' | 'quantityMaximum') => {
    const isPrimary = bin.binLocationId === PRIMARY_BIN_ID;

    // The Primary Bin is a derived remainder, not a real bin — it has no
    // stocking plan of its own and nothing here is ever persisted for it.
    if (isPrimary) {
      return (
        <Typography component="span" sx={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
          —
        </Typography>
      );
    }

    const value = thresholdFor(bin, field);
    const edited = isFieldEdited(bin, field);
    const min = minFor(bin);
    const max = maxFor(bin);
    const invalid = min !== null && max !== null && min > max;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {edited && (
          <Box component="span" sx={{ fontSize: '8px', color: COLORS.teal, fontWeight: 700 }}>
            ●
          </Box>
        )}
        <Box
          component="input"
          type="number"
          min={0}
          step={1}
          placeholder="—"
          value={value === null ? '' : String(value)}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(bin.binLocationId, field, e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault();
          }}
          sx={numberInputSx(edited, invalid)}
        />
      </Box>
    );
  };

  /** Same bar as the grid's "% Full" column, scoped to one bin. */
  const renderPercentFull = (bin: InventoryBin) => {
    if (bin.binLocationId === PRIMARY_BIN_ID) {
      return <Typography component="span" sx={{ fontSize: '0.82rem', color: '#cbd5e1' }}>—</Typography>;
    }

    const max = maxFor(bin);
    if (max === null || max <= 0) {
      return <Typography component="span" sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>—</Typography>;
    }

    const qty = qtyFor(bin);
    const min = minFor(bin);
    const pct = Math.min(100, Math.round((qty / max) * 100));
    const barColor = min !== null && qty < min ? BAR_BELOW_MIN : pct < 50 ? BAR_LOW : BAR_OK;

    return (
      <Box sx={{ width: '100%', px: 0.5 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 20,
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${pct}%`,
              height: '100%',
              backgroundColor: barColor,
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: '#1e293b',
            }}
          >
            {pct}%
          </Box>
        </Box>
      </Box>
    );
  };

  const rollUpMessage = (): { text: string; tone: 'warn' | 'error' } | null => {
    if (!isManaging) return null;

    if (consignmentBlocked) {
      return {
        tone: 'error',
        text:
          'Bin Min/Max totals exceed this item\'s Location Min/Max. Location Min/Max cannot be '
          + 'changed on consignment inventory, so this cannot be saved — lower the bin values.',
      };
    }

    if (willCreateLocationMin) {
      return {
        tone: 'warn',
        text:
          `This item has no Location Min set. Saving will set it to ${formatQty(binMinTotal)}, `
          + 'which turns on low-stock alerts for this item.',
      };
    }

    if (willRaiseMax && willRaiseMin) {
      return {
        tone: 'warn',
        text:
          `Bin totals are above the Location levels. Saving will raise Location Min to `
          + `${formatQty(binMinTotal)} and Location Max to ${formatQty(binMaxTotal)}.`,
      };
    }

    if (willRaiseMax) {
      return {
        tone: 'warn',
        text:
          `Bin maximums total ${formatQty(binMaxTotal)}, above the Location Max of `
          + `${formatThreshold(locationMax)}. Saving will raise the Location Max to `
          + `${formatQty(binMaxTotal)}.`,
      };
    }

    if (willRaiseMin) {
      return {
        tone: 'warn',
        text:
          `Bin minimums total ${formatQty(binMinTotal)}, above the Location Min of `
          + `${formatThreshold(locationMin)}. Saving will raise the Location Min to `
          + `${formatQty(binMinTotal)}.`,
      };
    }

    return null;
  };

  const notice = rollUpMessage();

  return (
    <Box sx={{ backgroundColor: '#f8fafc', borderLeft: `3px solid ${COLORS.teal}` }}>
      <Box sx={{ width: '100%', maxHeight: 300, overflowY: 'auto' }}>
        <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: COLORS.teal }}>
              <TableCell sx={{ ...headerCellSx, width: COL_WIDTHS.name, pl: 2 }}>
                Bin Location
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: COL_WIDTHS.quantity }} align="center">
                Quantity{isManaging && canEdit ? ' ✎' : ''}
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: COL_WIDTHS.min }} align="center">
                Min{isManaging && canEdit ? ' ✎' : ''}
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: COL_WIDTHS.max }} align="center">
                Max{isManaging && canEdit ? ' ✎' : ''}
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: COL_WIDTHS.pctFull }} align="center">
                % Full
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: COL_WIDTHS.spacer }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {bins.map((bin) => {
              const isPrimary = bin.binLocationId === PRIMARY_BIN_ID;
              return (
                <TableRow
                  key={bin.binLocationId}
                  sx={{
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: '#f8fafc' },
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                >
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      pl: 2,
                      fontWeight: isPrimary ? 600 : 400,
                      color: isPrimary ? COLORS.teal : '#374151',
                    }}
                  >
                    {bin.binLocationName}
                    {isPrimary && (
                      <Typography
                        component="span"
                        sx={{ ml: 1, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 400 }}
                      >
                        (unallocated)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {renderQuantity(bin)}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {renderThreshold(bin, 'quantityMinimum')}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {renderThreshold(bin, 'quantityMaximum')}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {renderPercentFull(bin)}
                  </TableCell>
                  <TableCell sx={bodyCellSx} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {isManaging && (
        <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
          {/* Totals row — the containment rule, visible while the user types. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              px: 2,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', color: overAllocated ? '#c62828' : '#64748b' }}>
              {overAllocated
                ? `Over-allocated by ${formatQty(Math.abs(unallocated))} — bins cannot exceed the ${formatQty(quantityOnHand)} on hand`
                : `${formatQty(allocated)} of ${formatQty(quantityOnHand)} allocated · ${formatQty(unallocated)} unallocated`}
            </Typography>

            <Typography sx={{ fontSize: '0.78rem', color: willRaiseMin ? '#b45309' : '#64748b' }}>
              Bin Min total <strong>{formatQty(binMinTotal)}</strong> of Location Min{' '}
              <strong>{formatThreshold(locationMin)}</strong>
            </Typography>

            <Typography sx={{ fontSize: '0.78rem', color: willRaiseMax ? '#b45309' : '#64748b' }}>
              Bin Max total <strong>{formatQty(binMaxTotal)}</strong> of Location Max{' '}
              <strong>{formatThreshold(locationMax)}</strong>
            </Typography>
          </Box>

          {(notice || invalidBin || saveError || savedNotice) && (
            <Box sx={{ px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {invalidBin && (
                <Typography sx={{ fontSize: '0.78rem', color: '#c62828', fontWeight: 600 }}>
                  Bin {invalidBin.binLocationName}: Min cannot exceed Max
                </Typography>
              )}
              {notice && (
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: notice.tone === 'error' ? '#c62828' : '#b45309',
                  }}
                >
                  {notice.text}
                </Typography>
              )}
              {saveError && (
                <Typography sx={{ fontSize: '0.78rem', color: '#c62828', fontWeight: 600 }}>
                  {saveError}
                </Typography>
              )}
              {savedNotice && (
                <Typography sx={{ fontSize: '0.78rem', color: COLORS.teal, fontWeight: 600 }}>
                  {savedNotice}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', px: 2, pb: 1.25 }}>
            <Button
              size="small"
              onClick={handleCancel}
              disabled={saving}
              sx={{ textTransform: 'none', fontSize: '0.8rem', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={!hasEdits || overAllocated || !!invalidBin || consignmentBlocked || saving}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                backgroundColor: COLORS.teal,
                '&:hover': { backgroundColor: COLORS.teal },
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
